/**
 * API service for Kanban Board operations
 * Handles CRUD operations for boards, columns, and access control
 */

/**
 * Fetch all boards accessible to the current user
 * @param {Function} apiFetch - The authenticated API fetch function from useAuth
 * @returns {Promise<Array>} Array of boards
 */
export const fetchBoards = async (apiFetch) => {
    const response = await apiFetch('/api/KanbanBoards');
    const data = await response.json();
    if (data.success) {
        return data.data || [];
    }
    throw new Error(data.message || 'Failed to fetch boards');
};

/**
 * Fetch all boards for admin board management.
 * @param {Function} apiFetch - The authenticated API fetch function from useAuth
 * @returns {Promise<Array>} Array of boards
 */
export const fetchAllBoardsAdmin = async (apiFetch) => {
    const response = await apiFetch('/api/KanbanBoards/admin/all-boards');
    const data = await response.json();
    if (data.success) {
        return data.data || [];
    }
    throw new Error(data.message || 'Failed to fetch all boards');
};

/**
 * Fetch details of a specific board including columns and access grants
 * @param {Function} apiFetch - The authenticated API fetch function from useAuth
 * @param {number} boardId - The ID of the board to fetch
 * @returns {Promise<Object>} Board details
 */
export const fetchBoard = async (apiFetch, boardId) => {
    const response = await apiFetch(`/api/KanbanBoards/${boardId}`);
    if (!response.ok) {
        throw new Error(`Failed to fetch board details (HTTP ${response.status})`);
    }
    const data = await response.json();
    if (data.success) {
        return data.data;
    }
    throw new Error(data.message || 'Failed to fetch board details');
};

/**
 * Create a new Kanban board
 * @param {Function} apiFetch - The authenticated API fetch function from useAuth
 * @param {Object} boardData - Board creation data
 * @returns {Promise<Object>} Created board data
 */
export const createBoard = async (apiFetch, boardData) => {
    const response = await apiFetch('/api/KanbanBoards', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(boardData)
    });
    const data = await response.json();
    if (data.success) {
        return data.data;
    }
    throw new Error(data.message || 'Failed to create board');
};

/**
 * Update an existing board
 * @param {Function} apiFetch - The authenticated API fetch function from useAuth
 * @param {number} boardId - The ID of the board to update
 * @param {Object} updates - Updated board data
 * @returns {Promise<Object>} Update response
 */
export const updateBoard = async (apiFetch, boardId, updates) => {
    const response = await apiFetch(`/api/KanbanBoards/${boardId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
    });
    if (!response.ok) {
        if (response.status === 403) {
            throw new Error('You do not have permission to modify this board\'s settings');
        }
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to update board');
    }
    const data = await response.json();
    if (data.success) {
        return data;
    }
    throw new Error(data.message || 'Failed to update board');
};

/**
 * Delete a board
 * @param {Function} apiFetch - The authenticated API fetch function from useAuth
 * @param {number} boardId - The ID of the board to delete
 * @returns {Promise<Object>} Delete response
 */
export const deleteBoard = async (apiFetch, boardId) => {
    const response = await apiFetch(`/api/KanbanBoards/${boardId}`, {
        method: 'DELETE'
    });
    const data = await response.json().catch(() => ({}));
    if (data.success) {
        return data;
    }
    throw new Error(data.message || 'Failed to delete board');
};

/**
 * Add a column to a board
 * @param {Function} apiFetch - The authenticated API fetch function from useAuth
 * @param {number} boardId - The ID of the board
 * @param {Object} columnData - Column data
 * @returns {Promise<Object>} Created column data
 */
export const addColumn = async (apiFetch, boardId, columnData) => {
    const response = await apiFetch(`/api/KanbanBoards/${boardId}/columns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(columnData)
    });
    const data = await response.json();
    if (data.success) {
        return data.data;
    }
    throw new Error(data.message || 'Failed to add column');
};

/**
 * Update a column
 * @param {Function} apiFetch - The authenticated API fetch function from useAuth
 * @param {number} boardId - The ID of the board
 * @param {number} columnId - The ID of the column to update
 * @param {Object} updates - Updated column data
 * @returns {Promise<Object>} Update response
 */
export const updateColumn = async (apiFetch, boardId, columnId, updates) => {
    const response = await apiFetch(`/api/KanbanBoards/${boardId}/columns/${columnId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
    });
    const data = await response.json();
    if (data.success) {
        return data;
    }
    throw new Error(data.message || 'Failed to update column');
};

/**
 * Delete a column
 * @param {Function} apiFetch - The authenticated API fetch function from useAuth
 * @param {number} boardId - The ID of the board
 * @param {number} columnId - The ID of the column to delete
 * @returns {Promise<Object>} Delete response
 */
export const deleteColumn = async (apiFetch, boardId, columnId) => {
    const response = await apiFetch(`/api/KanbanBoards/${boardId}/columns/${columnId}`, {
        method: 'DELETE'
    });
    const data = await response.json();
    if (data.success) {
        return data;
    }
    throw new Error(data.message || 'Failed to delete column');
};

/**
 * Grant access to a user for a board
 * @param {Function} apiFetch - The authenticated API fetch function from useAuth
 * @param {number} boardId - The ID of the board
 * @param {Object} accessData - Access grant data (userId, permissionLevel)
 * @returns {Promise<Object>} Grant response
 */
export const grantAccess = async (apiFetch, boardId, accessData) => {
    const response = await apiFetch(`/api/KanbanBoards/${boardId}/access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(accessData)
    });
    const data = await response.json();
    if (data.success) {
        return data;
    }
    throw new Error(data.message || 'Failed to grant access');
};

/**
 * Revoke access from a user for a board
 * @param {Function} apiFetch - The authenticated API fetch function from useAuth
 * @param {number} boardId - The ID of the board
 * @param {number} accessId - The ID of the access grant to revoke
 * @returns {Promise<Object>} Revoke response
 */
export const revokeAccess = async (apiFetch, boardId, accessId) => {
    const response = await apiFetch(`/api/KanbanBoards/${boardId}/access/${accessId}`, {
        method: 'DELETE'
    });
    const data = await response.json();
    if (data.success) {
        return data;
    }
    throw new Error(data.message || 'Failed to revoke access');
};

/**
 * Fetch all available board templates
 * @param {Function} apiFetch - The authenticated API fetch function from useAuth
 * @returns {Promise<Array>} Array of templates
 */
export const fetchTemplates = async (apiFetch) => {
    const response = await apiFetch('/api/KanbanBoards/templates');
    const data = await response.json();
    if (data.success) {
        return data.data || [];
    }
    throw new Error(data.message || 'Failed to fetch templates');
};

/**
 * Fetch details of a specific template including columns
 * @param {Function} apiFetch - The authenticated API fetch function from useAuth
 * @param {number} templateId - The ID of the template to fetch
 * @returns {Promise<Object>} Template details
 */
export const fetchTemplate = async (apiFetch, templateId) => {
    const response = await apiFetch(`/api/KanbanBoards/templates/${templateId}`);
    const data = await response.json();
    if (data.success) {
        return data.data;
    }
    throw new Error(data.message || 'Failed to fetch template details');
};

/**
 * Create a board from a template
 * @param {Function} apiFetch - The authenticated API fetch function from useAuth
 * @param {number} templateId - The ID of the template to use
 * @param {Object} boardData - Board creation data (name, description, isPublic, propertyId)
 * @returns {Promise<Object>} Created board data
 */
export const createBoardFromTemplate = async (apiFetch, templateId, boardData) => {
    const response = await apiFetch(`/api/KanbanBoards/from-template/${templateId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(boardData)
    });
    const data = await response.json();
    if (data.success) {
        return data.data;
    }
    throw new Error(data.message || 'Failed to create board from template');
};

/**
 * Duplicate an existing board
 * @param {Function} apiFetch - The authenticated API fetch function from useAuth
 * @param {number} boardId - The ID of the board to duplicate
 * @param {Object} boardData - Board duplication data (name, isPublic, copyAccessGrants)
 * @returns {Promise<Object>} Duplicated board data
 */
export const duplicateBoard = async (apiFetch, boardId, boardData) => {
    const response = await apiFetch(`/api/KanbanBoards/${boardId}/duplicate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(boardData)
    });
    const data = await response.json();
    if (data.success) {
        return data.data;
    }
    throw new Error(data.message || 'Failed to duplicate board');
};

/**
 * Save a board as a template
 * @param {Function} apiFetch - The authenticated API fetch function from useAuth
 * @param {number} boardId - The ID of the board to save as template
 * @param {Object} templateData - Template data (name, description, category, isPublic)
 * @returns {Promise<Object>} Created template data
 */
export const saveBoardAsTemplate = async (apiFetch, boardId, templateData) => {
    const response = await apiFetch(`/api/KanbanBoards/${boardId}/save-as-template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(templateData)
    });
    const data = await response.json();
    if (data.success) {
        return data.data;
    }
    throw new Error(data.message || 'Failed to save board as template');
};

/**
 * Fetch activity for a board
 * @param {Function} apiFetch - The authenticated API fetch function from useAuth
 * @param {number} boardId - The ID of the board
 * @param {Object} params - Query parameters (page, pageSize, activityType, fromDate, toDate)
 * @returns {Promise<Object>} Activity feed data with pagination
 */
export const fetchBoardActivity = async (apiFetch, boardId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await apiFetch(`/api/BoardAnalytics/boards/${boardId}/activity?${queryString}`);
    const data = await response.json();
    if (data.success) {
        return data.data;
    }
    throw new Error(data.message || 'Failed to fetch board activity');
};

/**
 * Fetch statistics for a board
 * @param {Function} apiFetch - The authenticated API fetch function from useAuth
 * @param {number} boardId - The ID of the board
 * @param {Object} params - Query parameters (fromDate, toDate)
 * @returns {Promise<Object>} Board statistics
 */
export const fetchBoardStatistics = async (apiFetch, boardId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await apiFetch(`/api/BoardAnalytics/boards/${boardId}/statistics?${queryString}`);
    const data = await response.json();
    if (data.success) {
        return data.data;
    }
    throw new Error(data.message || 'Failed to fetch board statistics');
};

/**
 * Fetch trends for a board
 * @param {Function} apiFetch - The authenticated API fetch function from useAuth
 * @param {number} boardId - The ID of the board
 * @param {Object} params - Query parameters (fromDate, toDate, days)
 * @returns {Promise<Object>} Board trends data
 */
export const fetchBoardTrends = async (apiFetch, boardId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await apiFetch(`/api/BoardAnalytics/boards/${boardId}/trends?${queryString}`);
    const data = await response.json();
    if (data.success) {
        return data.data;
    }
    throw new Error(data.message || 'Failed to fetch board trends');
};

/**
 * Fetch all swimlanes for a board
 */
export const fetchSwimlanes = async (apiFetch, boardId) => {
    const response = await apiFetch(`/api/ActionItems/boards/${boardId}/swimlanes`);
    const data = await response.json();
    if (!data.success) {
        throw new Error(data.message || 'Failed to fetch swimlanes');
    }
    return data.data || [];
};

/**
 * Create a new swimlane
 */
export const createSwimlane = async (apiFetch, boardId, swimlaneData) => {
    const response = await apiFetch(`/api/ActionItems/boards/${boardId}/swimlanes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(swimlaneData)
    });
    const data = await response.json();
    if (!data.success) {
        throw new Error(data.message || 'Failed to create swimlane');
    }
    return data.data;
};

/**
 * Update an existing swimlane
 */
export const updateSwimlane = async (apiFetch, boardId, swimlaneId, updates) => {
    const response = await apiFetch(`/api/ActionItems/boards/${boardId}/swimlanes/${swimlaneId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
    });
    const data = await response.json();
    if (!data.success) {
        throw new Error(data.message || 'Failed to update swimlane');
    }
    return data.data;
};

/**
 * Delete a swimlane
 */
export const deleteSwimlane = async (apiFetch, boardId, swimlaneId) => {
    const response = await apiFetch(`/api/ActionItems/boards/${boardId}/swimlanes/${swimlaneId}`, {
        method: 'DELETE'
    });
    const data = await response.json();
    if (!data.success) {
        throw new Error(data.message || 'Failed to delete swimlane');
    }
    return data;
};

/**
 * Reorder swimlanes
 */
export const reorderSwimlanes = async (apiFetch, boardId, swimlanes) => {
    const response = await apiFetch(`/api/ActionItems/boards/${boardId}/swimlanes/reorder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            swimlanes: swimlanes.map(s => ({
                boardSwimlaneID: s.boardSwimlaneID,
                sortOrder: s.sortOrder
            }))
        })
    });
    const data = await response.json();
    if (!data.success) {
        throw new Error(data.message || 'Failed to reorder swimlanes');
    }
    return data;
};

/**
 * Bulk migrate all action items from one board to another
 * @param {Function} apiFetch - The authenticated API fetch function from useAuth
 * @param {number} sourceBoardId - The source board ID
 * @param {number} targetBoardId - The target board ID
 * @returns {Promise<Object>} Migration result with migratedCount
 */
export const bulkMigrateTasks = async (apiFetch, sourceBoardId, targetBoardId) => {
    const response = await apiFetch('/api/KanbanBoards/admin/bulk-migrate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sourceBoardId, targetBoardId })
    });
    const data = await response.json();
    if (!data.success) {
        throw new Error(data.message || 'Failed to migrate action items');
    }
    return data;
};

/**
 * Get count of action items matching filter criteria
 * @param {Function} apiFetch
 * @param {Object} filters - TaskFilterCriteria fields
 * @returns {Promise<Object>} { count }
 */
export const getFilteredTaskCount = async (apiFetch, filters) => {
    const response = await apiFetch('/api/KanbanBoards/admin/task-count', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filters })
    });
    const data = await response.json();
    if (!data.success) {
        throw new Error(data.message || 'Failed to get task count');
    }
    return data.data;
};

/**
 * Bulk migrate action items matching filters to a target board
 * @param {Function} apiFetch
 * @param {Object} filters - TaskFilterCriteria fields
 * @param {number} targetBoardId
 * @returns {Promise<Object>} Migration result
 */
export const bulkMigrateFiltered = async (apiFetch, filters, targetBoardId) => {
    const response = await apiFetch('/api/KanbanBoards/admin/bulk-migrate-filtered', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filters, targetBoardId })
    });
    const data = await response.json();
    if (!data.success) {
        throw new Error(data.message || 'Failed to migrate action items');
    }
    return data;
};

/**
 * Bulk update action items matching filters
 * @param {Function} apiFetch
 * @param {Object} filters - TaskFilterCriteria fields
 * @param {Object} updates - { newStatus, newPriority, newAssignedToUserId, newBoardId }
 * @returns {Promise<Object>} Update result
 */
export const bulkUpdateTasks = async (apiFetch, filters, updates) => {
    const response = await apiFetch('/api/KanbanBoards/admin/bulk-update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filters, ...updates })
    });
    const data = await response.json();
    if (!data.success) {
        throw new Error(data.message || 'Failed to bulk update action items');
    }
    return data;
};

/**
 * Get the status of a bulk operation job
 * @param {Function} apiFetch
 * @param {string} jobId
 * @returns {Promise<Object>} Job status { jobId, status, affectedCount, message, queuedAt, completedAt }
 */
export const getBulkJobStatus = async (apiFetch, jobId) => {
    const response = await apiFetch(`/api/KanbanBoards/admin/bulk-job/${jobId}`);
    const data = await response.json();
    if (!data.success) {
        throw new Error(data.message || 'Failed to get job status');
    }
    return data.data;
};

/**
 * Poll a bulk job until it completes or fails.
 * @param {Function} apiFetch
 * @param {string} jobId
 * @param {Function} onProgress - Optional callback(status) called on each poll
 * @param {number} intervalMs - Polling interval (default 1000ms)
 * @returns {Promise<Object>} Final job status
 */
export const pollBulkJob = async (apiFetch, jobId, onProgress, intervalMs = 1000) => {
    while (true) {
        const status = await getBulkJobStatus(apiFetch, jobId);
        if (onProgress) onProgress(status);
        if (status.status === 'Completed' || status.status === 'Failed') {
            return status;
        }
        await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
};
